import React, { useEffect, useState } from 'react';
import { twMerge } from 'utilities/src/twMerge';
import { getPaginationWithEllipsis, calculateLastPage, MAX_VISIBLE_PAGES_BUTTONS } from 'utilities/src/pagination';
import { ThemeEnum } from '../Theme';
import ArrowRightBigIcon from '../../icons/ArrowRightBigIcon';
import ArrowRightLastIcon from '../../icons/ArrowRightLastIcon';
import ArrowLeftFirstIcon from '../../icons/ArrowLeftFirstIcon';
import ArrowLeftBigIcon from '../../icons/ArrowLeftBigIcon';

import Button from '../Button';
import LinkConditionalWrapper from './LinkConditionalWrapper';

type CustomLinkComponent<T> = [T] extends [string]
  ? { LinkComponent?: React.FC<{ href: T; children: React.ReactElement }> }
  : { LinkComponent: React.FC<{ href: T; children: React.ReactElement }> };

export type LinkComponentProps<T> = { hrefConstructor: (page: number) => T } & CustomLinkComponent<T>;

export type PaginationProps<T> = {
  total: number;
  itemsPerPage: number;
  onChangePage?: (newPage: number) => void;
  currentPage: number;
  maxVisiblePageButtons?: number;
  classes?: string;
  fisrtLastArrow?: boolean;
  showArrow?: string;
} & LinkComponentProps<T>;

function DefaultLinkComponent({ href, children }: { href: string; children: React.ReactElement }) {
  return <a href={href}>{children}</a>;
}

const buttonColorClasses =
  'disabled:!text-site-neutrals-100 !bg-transparent hover:!bg-site-primary-25 hover:!text-site-neutrals-900';

export default function Pagination<T>({
  total,
  itemsPerPage,
  onChangePage,
  currentPage,
  classes,
  maxVisiblePageButtons = MAX_VISIBLE_PAGES_BUTTONS,
  hrefConstructor,
  LinkComponent,
  fisrtLastArrow,
  showArrow = 'hidden',
}: PaginationProps<T>) {
  const [selectedPage, setSelectedPage] = useState(currentPage);
  useEffect(() => {
    setSelectedPage(currentPage);
  }, [currentPage]);

  if (total <= itemsPerPage) return null;

  const lastPage = calculateLastPage(total, itemsPerPage);
  const { paginationRange, isEllipsis } = getPaginationWithEllipsis({
    selectedPage,
    lastPage,
    maxVisiblePageButtons,
  });

  const onClick = (newPage: number) => () => {
    setSelectedPage(newPage);
    onChangePage?.(newPage);
  };

  const linkProps = (page: number) =>
    LinkComponent === undefined
      ? { Component: DefaultLinkComponent, href: hrefConstructor(page) }
      : { Component: LinkComponent, href: hrefConstructor(page) };

  return (
    <nav className={twMerge('flex justify-center', classes)}>
      <ul className="flex">
        {fisrtLastArrow && (
          <li key="fisrt">
            <LinkConditionalWrapper
              wrap={selectedPage !== 1}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...linkProps(currentPage - 1)}
            >
              <Button<ThemeEnum.Site>
                icon={ArrowLeftFirstIcon}
                size="Small"
                type="button"
                onClick={onClick(1)}
                disabled={selectedPage === 1}
                ariaLabel="first page"
                classes={twMerge('mr-1 w-10 h-10 hidden mdS:flex', buttonColorClasses)}
              />
            </LinkConditionalWrapper>
          </li>
        )}

        <li>
          <LinkConditionalWrapper
            wrap={selectedPage !== 1}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...linkProps(currentPage - 1)}
          >
            <Button<ThemeEnum.Site>
              icon={ArrowLeftBigIcon}
              size="Small"
              type="button"
              onClick={onClick(selectedPage - 1)}
              disabled={selectedPage === 1}
              ariaLabel="previous page"
              classes={twMerge('mr-1 w-10 h-10 mdS:flex', buttonColorClasses, showArrow)}
            />
          </LinkConditionalWrapper>
        </li>
        {paginationRange.map((pageButton, index) => (
          <li key={`page-${pageButton}`}>
            {isEllipsis(index) ? (
              <div className="text-site-neutrals-900 mx-1 flex h-10 w-10 items-center justify-center">...</div>
            ) : (
              <LinkConditionalWrapper
                wrap={selectedPage !== pageButton}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...linkProps(pageButton)}
              >
                <Button<ThemeEnum.Site>
                  size="Small"
                  classes={twMerge(
                    {
                      'border-2 border-site-neutrals-600 !bg-site-neutrals-600 !text-site-neutrals-0 !opacity-100':
                        pageButton === selectedPage,
                      [buttonColorClasses]: pageButton !== selectedPage,
                    },
                    'mx-1 w-10 h-10',
                  )}
                  type="button"
                  onClick={onClick(pageButton)}
                  ariaLabel="pagination"
                  disabled={pageButton === selectedPage}
                >
                  {pageButton}
                </Button>
              </LinkConditionalWrapper>
            )}
          </li>
        ))}
        <li>
          <LinkConditionalWrapper
            wrap={selectedPage !== lastPage}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...linkProps(currentPage + 1)}
          >
            <Button<ThemeEnum.Site>
              icon={ArrowRightBigIcon}
              size="Small"
              type="button"
              onClick={onClick(selectedPage + 1)}
              disabled={selectedPage === lastPage}
              ariaLabel="next page"
              classes={twMerge('ml-1 w-10 h-10 mdS:flex', buttonColorClasses, showArrow)}
            />
          </LinkConditionalWrapper>
        </li>
        {fisrtLastArrow && (
          <li key="last">
            <LinkConditionalWrapper
              wrap={selectedPage !== lastPage}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...linkProps(currentPage + 1)}
            >
              <Button<ThemeEnum.Site>
                icon={ArrowRightLastIcon}
                size="Small"
                type="button"
                onClick={onClick(lastPage)}
                disabled={selectedPage === lastPage}
                ariaLabel="last page"
                classes={twMerge('ml-1 w-10 h-10 hidden mdS:flex', buttonColorClasses)}
              />
            </LinkConditionalWrapper>
          </li>
        )}
      </ul>
    </nav>
  );
}
